/**
 * Crew model (P9 pilot)
 * ─────────────────────
 * Contractor-owned crew rosters. INFORMATIONAL ONLY for pilot:
 *   • read-only
 *   • no scoring, no tier, no ranking
 *   • zero influence on dispatch intelligence
 *   • contractor-level tier remains the sole gating mechanism
 *
 * Hidden entirely behind FEATURE_FLAGS.crewInsightsEnabled.
 */

import type { Job } from "@/data/types";

export interface Crew {
  id: string;            // CREW-XXX
  contractorId: string;  // FK → Contractor.id
  name: string;
  leadInstallerName: string;
  members: string[];     // names only for pilot, not user records
  active: boolean;
}

export const CREW_METRICS_MIN_SAMPLE = 3;

export interface CrewMetrics {
  completedCount: number;
  /** qa_passed / (qa_passed + qa_failed). null when n<3 reviewed. */
  qaPassRate: number | null;
  /** completed-on-or-before-scheduled / completed. null when n<3. */
  onTimeCompletionRate: number | null;
}

/* ── Selectors ─────────────────────────────────────────────────────── */

export function getCrewsForContractor(crews: Crew[], contractorId: string): Crew[] {
  if (!contractorId) return [];
  return crews.filter((c) => c.contractorId === contractorId);
}

/**
 * Derive read-only metrics for a single crew from jobs only.
 * No mutations. No external state.
 *
 * On-time completion: getJobCompletionDate(job) <= scheduledInstallDate.
 */
export function getCrewMetrics(args: {
  crewId: string;
  jobs: Job[];
  // Lazy import boundary: callers pass the helper to avoid circular deps.
  getCompletionDate: (j: Job) => string | null;
  getJobQaCounts: (j: Job) => { passed: number; failed: number };
}): CrewMetrics {
  const { crewId, jobs, getCompletionDate, getJobQaCounts } = args;
  const crewJobs = jobs.filter(
    (j) => (j as Job & { crewId?: string }).crewId === crewId,
  );

  let completedCount = 0;
  let onTimeCount = 0;
  let onTimeObservable = 0;
  let qaPassed = 0;
  let qaFailed = 0;

  for (const job of crewJobs) {
    const completion = getCompletionDate(job);
    if (completion) {
      completedCount++;
      if (job.scheduledInstallDate) {
        onTimeObservable++;
        if (completion <= job.scheduledInstallDate) onTimeCount++;
      }
    }
    const { passed, failed } = getJobQaCounts(job);
    qaPassed += passed;
    qaFailed += failed;
  }

  const qaTotal = qaPassed + qaFailed;
  return {
    completedCount,
    qaPassRate:
      qaTotal >= CREW_METRICS_MIN_SAMPLE ? qaPassed / qaTotal : null,
    onTimeCompletionRate:
      onTimeObservable >= CREW_METRICS_MIN_SAMPLE
        ? onTimeCount / onTimeObservable
        : null,
  };
}

/* ── Pilot seed ────────────────────────────────────────────────────── */

export const seedCrews: Crew[] = [
  {
    id: "CREW-001",
    contractorId: "CON-001",
    name: "Reynolds Crew A",
    leadInstallerName: "Mike Reynolds",
    members: ["Mike Reynolds", "Devon Pierce"],
    active: true,
  },
  {
    id: "CREW-002",
    contractorId: "CON-001",
    name: "Crew B",
    leadInstallerName: "Carlos Mendez",
    members: ["Carlos Mendez", "Tomás Rivera"],
    active: true,
  },
];

/**
 * Map of jobId → crewId for the pilot. Applied as a non-invasive overlay
 * (same pattern as applyContractorPortalDemo).
 */
export const SEED_CREW_ASSIGNMENTS: Record<string, string> = {
  "JOB-008": "CREW-001",
  "JOB-013": "CREW-001",
  "JOB-009": "CREW-001",
  "JOB-016": "CREW-002",
  "JOB-018": "CREW-002",
  "JOB-020": "CREW-002",
};

export function applyCrewAssignments(jobs: Job[]): Job[] {
  return jobs.map((j) => {
    const crewId = SEED_CREW_ASSIGNMENTS[j.id];
    if (!crewId) return j;
    return { ...j, crewId } as Job;
  });
}
